import { StrictMode, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AppProviders } from '@shared/app/providers';
import { AppConfigProvider, type AppConfig } from '@shared/app/app-config';
import { buildAppRoutes, type AppRouteOptions } from '@shared/router/build-routes';
import { configureAccessChannel } from '@shared/domain/access/channel';

interface AppRootProps {
  config: AppConfig;
  routes: AppRouteOptions;
}

/**
 * The root of a HEXAUNITY application: shared providers (theme, query, the
 * Identity session, tooltips, toasts), the application's own configuration,
 * and the shared route tree around its screens.
 */
export function AppRoot({ config, routes }: AppRootProps) {
  const router = useMemo(() => createBrowserRouter(buildAppRoutes(routes)), [routes]);
  return (
    <AppConfigProvider config={config}>
      <AppProviders>
        <RouterProvider router={router} />
      </AppProviders>
    </AppConfigProvider>
  );
}

/** Mount an application into `#root`. */
export function mountApp(config: AppConfig, routes: AppRouteOptions): void {
  // Access administration goes through Finance `/access/*` in the Finance
  // application and directly to identity everywhere else.
  configureAccessChannel(config.application);
  const container = document.getElementById('root');
  if (!container) throw new Error('Root element "#root" was not found in the document.');
  document.title = `${config.name} · HEXAUNITY`;
  createRoot(container).render(
    <StrictMode>
      <AppRoot config={config} routes={routes} />
    </StrictMode>,
  );
}
